import React, { useState } from 'react';
import './PlayerPropsCard.css';

const PlayerPropsCard = ({ data }) => {
  const [activeTab, setActiveTab] = useState('overview');
  
  if (!data || data.type !== 'player_props_card') return null;
  
  const { card_title, player, opponent, injury_status, environment, tabs, data_freshness } = data;
  
  const getTabContent = (tabId) => tabs?.find(t => t.id === tabId)?.content;
  
  return (
    <div className="player-props-card">
      <div className="ppc-header">
        <h3>{card_title}</h3>
        {injury_status?.status !== 'ACTIVE' && (
          <span className="injury-badge">{injury_status.status}</span>
        )}
      </div>
      
      <div className="ppc-tabs">
        {tabs?.map(tab => (
          <button
            key={tab.id}
            className={`ppc-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      
      <div className="ppc-content">
        {activeTab === 'overview' && <OverviewTab content={getTabContent('overview')} opponent={opponent?.abbrev} />}
        {activeTab === 'combos' && <CombosTab content={getTabContent('combos')} opponent={opponent?.abbrev} />}
        {activeTab === 'game_log' && <GameLogTab content={getTabContent('game_log')} />}
        {activeTab === 'prop_check' && <PropCheckTab content={getTabContent('prop_check')} />}
      </div>
      
      <div className="ppc-footer">
        <span className="freshness">Updated: {new Date(data_freshness?.generated_at).toLocaleString()}</span>
      </div>
    </div>
  );
};

const OverviewTab = ({ content, opponent }) => {
  if (!content) return null;
  const { stat_grid, insights, warnings } = content;
  
  return (
    <div className="overview-tab">
      {warnings?.length > 0 && (
        <div className="warnings">
          {warnings.map((w, i) => (
            <div key={i} className={`warning ${w.severity}`}>⚠️ {w.text}</div>
          ))}
        </div>
      )}
      
      <div className="stat-grid-wrapper">
        <table className="stat-grid">
          <thead>
            <tr>
              {stat_grid?.columns?.map(col => (
                <th key={col.key}>
                  {col.label}
                  {col.n !== undefined && <span className="n-badge">N={col.n}</span>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {stat_grid?.rows?.map(row => (
              <tr key={row.stat_key}>
                <td className="stat-label">{row.label}</td>
                <td>{row.season}</td>
                <td className={getHeatClass(row.last5, row.season)}>{row.last5}</td>
                <td>{row.last10}</td>
                <td className={getHeatClass(row.vs_opp, row.season)}>{row.vs_opp}</td>
                <td>{row.vs_opp_recent}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {insights?.length > 0 && (
        <div className="insights">
          {insights.map((insight, i) => <div key={i} className="insight">{insight}</div>)}
        </div>
      )}
    </div>
  );
};

const CombosTab = ({ content, opponent }) => {
  if (!content) return null;
  const { combo_grid } = content;
  
  return (
    <div className="combos-tab">
      <table className="stat-grid">
        <thead>
          <tr>
            {combo_grid?.columns?.map(col => (
              <th key={col.key}>
                {col.label}
                {col.n !== undefined && <span className="n-badge">N={col.n}</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {combo_grid?.rows?.map(row => (
            <tr key={row.combo_key}>
              <td className="stat-label">{row.label}</td>
              <td className="formula">{row.formula}</td>
              <td>{row.season}</td>
              <td className={getHeatClass(row.last5, row.season)}>{row.last5}</td>
              <td>{row.last10}</td>
              <td className={getHeatClass(row.vs_opp, row.season)}>{row.vs_opp}</td>
              <td>{row.vs_opp_recent}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const GameLogTab = ({ content }) => {
  if (!content) return null;
  const { games, averages, sample_warning } = content;
  
  return (
    <div className="game-log-tab">
      {sample_warning && <div className="warning info">⚠️ {sample_warning}</div>}
      
      <table className="game-log-table">
        <thead>
          <tr>
            <th>Date</th><th>Matchup</th><th>W/L</th><th>MIN</th><th>PTS</th>
            <th>REB</th><th>AST</th><th>STL</th><th>BLK</th><th>3PM</th><th>TOV</th>
          </tr>
        </thead>
        <tbody>
          {games?.map((g, i) => (
            <tr key={i}>
              <td>{g.date}</td>
              <td>{g.matchup}</td>
              <td className={g.result === 'W' ? 'win' : 'loss'}>{g.result}</td>
              <td>{g.min}</td><td>{g.pts}</td><td>{g.reb}</td><td>{g.ast}</td>
              <td>{g.stl}</td><td>{g.blk}</td><td>{g.fg3m}</td><td>{g.tov}</td>
            </tr>
          ))}
          {averages && (
            <tr className="avg-row">
              <td colSpan="3"><strong>AVG</strong></td>
              <td>{averages.min}</td><td>{averages.pts}</td><td>{averages.reb}</td>
              <td>{averages.ast}</td><td>{averages.stl}</td><td>{averages.blk}</td>
              <td>{averages.fg3m}</td><td>{averages.tov}</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const PropCheckTab = ({ content }) => {
  if (!content) return null;
  const { status, cta, suggested_props, comparisons, lean } = content;
  
  return (
    <div className="prop-check-tab">
      {status === 'no_line_provided' ? (
        <>
          <p className="cta">{cta}</p>
          {suggested_props?.length > 0 && (
            <div className="suggested-props">
              <h4>Props to Watch</h4>
              {suggested_props.map((p, i) => (
                <div key={i} className="suggested-prop">
                  <span className="prop-label">{p.label}</span>
                  <span className="prop-reason">{p.reason}</span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {comparisons && (
            <div className="comparisons">
              <h4>Line Analysis</h4>
              <table className="comparison-table">
                <thead>
                  <tr><th>Window</th><th>Avg</th><th>vs Line</th><th>Hit Rate</th></tr>
                </thead>
                <tbody>
                  {comparisons.map((c, i) => (
                    <tr key={i}>
                      <td>{c.window}</td>
                      <td>{c.value}</td>
                      <td className={c.vs_line?.startsWith('+') ? 'positive' : 'negative'}>{c.vs_line}</td>
                      <td>{c.hit_rate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {lean && (
            <div className={`lean-box ${lean.direction?.toLowerCase()}`}>
              <span className="lean-direction">{lean.direction}</span>
              <span className="lean-confidence">({lean.confidence})</span>
              <p className="lean-reasoning">{lean.reasoning}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Helper: heat class for values above/below baseline
const getHeatClass = (value, baseline) => {
  if (!value || !baseline) return '';
  const pct = (value - baseline) / baseline;
  if (pct >= 0.15) return 'hot';
  if (pct <= -0.15) return 'cold';
  return '';
};

export default PlayerPropsCard;
